import React from 'react';
import { Box, Typography } from '@mui/material';
import { LuMenu, LuCheck, LuArrowRight, LuFlag } from 'react-icons/lu';
import { elliottTokens as t } from '../tokens';
import { LessonBadgeSource, LessonPillState, resolveLessonPill } from '../../../../../../utils/lessonBadges';
import type { Requirement } from '../../../../../../components/course-versioning/learnerWhatsNew';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../mobile/mobileApp';

export type LessonStatus = 'complete' | 'in-progress' | 'todo';

/** Matches the admin surfaces' red-flag colour (OrgMembers / assessment modal). */
const FLAG_RED = '#FF6A55';

export interface NavLesson {
  id: string;
  title: string;
  status: LessonStatus;
  /** Date-gated on the BE (level not active / task startDate in the future) — not clickable. */
  locked?: boolean;
  /** L-8: unresolved red flag — rendered BESIDE the completion tick, never replacing it. */
  flagged?: boolean;
  /** B4-5: added after the learner started the program and not opened yet (never on complete lessons). */
  isNew?: boolean;
  /** Course versioning: badge a publish stored on the learner's record ('new' also mirrors isNew). */
  badge?: 'new' | 'updated' | null;
  /** Where `badge` came from ('stored' = a publish, 'fallback' = the isNew rule only); absent on older BE builds. */
  badgeSource?: LessonBadgeSource;
  /** Course versioning: what a publish asked the learner to do here; required unless `optional`. */
  pendingAction?: { kind: string; optional: boolean } | null;
  /** Audience Requirements: what this learner must do here; Read Only has no Roleplay or Quiz (../lessonAccess.ts). */
  access?: 'readAssess' | 'readOnly';
}
export interface NavModule {
  id: string;
  short: string; // e.g. "M1"
  title: string; // e.g. "Module 1: Data Systems"
  status: LessonStatus; // module-level rollup for the collapsed bubble
  lessons: NavLesson[];
}

const fillFor = (status: LessonStatus) =>
  status === 'complete' ? t.navGreenFill : status === 'in-progress' ? t.navBrandyFill : 'none';

const textFor = (status: LessonStatus) =>
  status === 'complete' ? t.navGreenText : status === 'in-progress' ? t.navBrandyText : t.textTertiary;

// --- Collapsed: module number bubbles (106px rail) ---
const CollapsedRail: React.FC<{ modules: NavModule[]; onExpand: () => void }> = ({ modules, onExpand }) => (
  <Box sx={{ width: 106, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Box sx={{ height: 82, display: 'flex', alignItems: 'center' }}>
      <Box
        onClick={onExpand}
        sx={{ p: 1, cursor: 'pointer', color: t.textPrimary, display: 'flex' }}
        aria-label="Expand navigation"
      >
        <LuMenu size={32} />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pt: '16px' }}>
      {modules.map((m) => {
        const highlighted = m.status !== 'todo';
        const accent = m.status === 'complete' ? t.success : m.status === 'in-progress' ? t.brandLight : t.borderDim;
        const textFill = m.status === 'complete' ? t.greenTextGradient : t.brandyTextGradient;
        return (
          <Box
            key={m.id}
            onClick={onExpand}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              width: 56,
              height: 56,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              // Dark background (not a gradient fill) with a status-accent border + glow.
              bgcolor: t.bg,
              border: `1px solid ${accent}`,
              boxShadow: m.status === 'in-progress' ? `0 0 12px ${t.brandLight}66` : 'none'
            }}
          >
            <Typography
              sx={{
                position: 'relative',
                zIndex: 1,
                fontSize: 20,
                fontWeight: 600,
                // Brandy / green gradient text for highlighted modules; grey for to-do.
                ...(highlighted
                  ? {
                      backgroundImage: textFill,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent'
                    }
                  : { color: t.textTertiary })
              }}
            >
              {m.short}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

// --- Expanded: full module / lesson tree (280px panel) ---
// Status indicator: sharp white glyph on highlighted rows, dim dot when to-do.
const LessonIcon: React.FC<{ status: LessonStatus }> = ({ status }) => {
  if (status === 'complete') {
    return (
      <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LuCheck size={15} color={t.textPrimary} strokeWidth={3} />
      </Box>
    );
  }
  if (status === 'in-progress') {
    return (
      <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LuArrowRight size={15} color={t.textPrimary} strokeWidth={3} />
      </Box>
    );
  }
  return (
    <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.textTertiary }} />
    </Box>
  );
};

const LessonPill: React.FC<{ label: string; bg: string; sx?: object }> = ({ label, bg, sx }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      flexShrink: 0,
      height: 16,
      px: '6px',
      borderRadius: '8px',
      bgcolor: bg,
      color: '#FFFFFF',
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      ...sx
    }}
  >
    {label}
  </Box>
);

/** "New" pill: brandy accent (the in-progress colour), white uppercase text. */
export const NewLessonPill: React.FC<{ sx?: object }> = ({ sx }) => (
  <LessonPill label="New" bg={t.brandLight} sx={sx} />
);
/** "Updated" pill (course versioning): neutral, the lesson changed since the learner saw it. */
export const UpdatedLessonPill: React.FC<{ sx?: object }> = ({ sx }) => (
  <LessonPill label="Updated" bg={t.borderContrast} sx={sx} />
);
/** "Required" / "Optional" tag beside a New or Updated pill (course versioning): outlined, so it reads as a qualifier. */
const RequirementTag: React.FC<{ requirement: Requirement }> = ({ requirement }) => (
  <LessonPill
    label={requirement === 'required' ? 'Required' : 'Optional'}
    bg="transparent"
    sx={{
      boxSizing: 'border-box',
      border: `1px solid ${requirement === 'required' ? FLAG_RED : t.borderContrast}`,
      color: requirement === 'required' ? FLAG_RED : t.textTertiary
    }}
  />
);
/** The New / Updated pill with its Required / Optional tag, or nothing. */
export const LessonBadgePill: React.FC<{ pill: LessonPillState | null; sx?: object }> = ({ pill, sx }) => {
  if (!pill) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0, ...sx }}>
      {pill.kind === 'updated' ? <UpdatedLessonPill /> : <NewLessonPill />}
      {pill.requirement && <RequirementTag requirement={pill.requirement} />}
    </Box>
  );
};

const NO_SEEN = new Set<string>();

const LessonRow: React.FC<{ lesson: NavLesson; active?: boolean; onSelect?: (id: string) => void }> = ({
  lesson,
  active,
  onSelect
}) => {
  const highlighted = lesson.status !== 'todo';
  const locked = Boolean(lesson.locked);
  // The page already applied the localStorage seen-set to `isNew` / `badge`.
  const pill = resolveLessonPill({
    badge: lesson.badge,
    badgeSource: lesson.badgeSource,
    pendingAction: lesson.pendingAction,
    isNew: lesson.isNew,
    complete: lesson.status === 'complete',
    seen: NO_SEEN,
    taskId: lesson.id
  });
  return (
    <Box
      onClick={() => {
        if (!locked) onSelect?.(lesson.id);
      }}
      title={locked ? 'Available soon' : undefined}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        px: '12px',
        py: '8px',
        // Phone: a thumb-sized row.
        [MOBILE_SX]: { minHeight: 44, py: '10px' },
        borderRadius: '6px',
        cursor: locked ? 'not-allowed' : 'pointer',
        opacity: locked ? 0.45 : 1,
        // The lesson currently open gets a brandy ring so the learner always
        // knows where they are in the tree.
        border: `1px solid ${active ? t.brandLight : t.borderDim}`,
        // Highlighted rows layer the brandy/green gradient over the base background.
        backgroundImage: highlighted ? `${fillFor(lesson.status)}, linear-gradient(${t.bg}, ${t.bg})` : 'none',
        bgcolor: highlighted ? undefined : t.bg,
        '&:hover': highlighted || locked ? undefined : { borderColor: t.borderContrast }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexShrink: 0 }}>
        <LessonIcon status={lesson.status} />
      </Box>
      <Typography
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          lineHeight: 'normal',
          fontWeight: lesson.status === 'in-progress' ? 600 : 400,
          color: textFor(lesson.status),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          // Phone: the whole lesson name, over as many lines as it takes.
          [MOBILE_SX]: { fontSize: 14, lineHeight: 1.35, whiteSpace: 'normal', overflow: 'visible' }
        }}
      >
        {lesson.title}
      </Typography>
      {pill && (
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexShrink: 0 }}>
          <LessonBadgePill pill={pill} />
        </Box>
      )}
      {/* L-8: complete-but-not-green — the red flag sits beside the tick,
          telling the learner this lesson has something to come back for. */}
      {lesson.flagged && (
        <Box
          sx={{ position: 'relative', zIndex: 1, display: 'flex', flexShrink: 0 }}
          title="This lesson has an unresolved item — open Improve Performance to retake it"
        >
          <LuFlag size={13} color={FLAG_RED} fill={FLAG_RED} strokeWidth={2} />
        </Box>
      )}
    </Box>
  );
};

const ExpandedPanel: React.FC<{
  modules: NavModule[];
  activeLessonId?: string;
  onCollapse: () => void;
  onSelectLesson?: (id: string) => void;
  /** Phone: the program's name over its modules. */
  title?: string;
}> = ({ modules, activeLessonId, onCollapse, onSelectLesson, title }) => (
  <Box
    sx={{
      width: 280,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      [MOBILE_SX]: { width: '100%' }
    }}
  >
    <Box
      sx={{
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: `1px solid ${t.borderDim}`,
        // Phone: the lesson's header keeps its menu above the tree, and closes it from there.
        [MOBILE_SX]: { display: 'none' }
      }}
    >
      <Box
        onClick={onCollapse}
        sx={{ p: 1, cursor: 'pointer', color: t.textPrimary, display: 'flex' }}
        aria-label="Collapse navigation"
      >
        <LuMenu size={32} />
      </Box>
    </Box>
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        [MOBILE_SX]: { p: '16px', gap: '20px' }
      }}
    >
      {title && (
        <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3, color: t.textPrimary }}>{title}</Typography>
      )}
      {modules.map((m, idx) => (
        <React.Fragment key={m.id}>
          {idx > 0 && <Box sx={{ height: '1px', bgcolor: t.borderDim, width: '100%' }} />}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: t.textPrimary }}>{m.title}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              {m.lessons.map((l) => (
                <LessonRow key={l.id} lesson={l} active={l.id === activeLessonId} onSelect={onSelectLesson} />
              ))}
            </Box>
          </Box>
        </React.Fragment>
      ))}
    </Box>
  </Box>
);

const LeftNav: React.FC<{
  modules: NavModule[];
  expanded: boolean;
  activeLessonId?: string;
  onToggle: (expanded: boolean) => void;
  onSelectLesson?: (id: string) => void;
  /** Phone: the program's name, at the top of the lesson tree. */
  title?: string;
}> = ({ modules, expanded, activeLessonId, onToggle, onSelectLesson, title }) => {
  const isMobileApp = useIsMobileApp();
  // Phone: no rail beside the lesson (the header's menu opens the tree), and the
  // tree covers the lesson under the header until a lesson is picked or the menu closes it.
  if (isMobileApp) {
    if (!expanded) return null;
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: t.bg,
          pb: 'env(safe-area-inset-bottom)'
        }}
      >
        <ExpandedPanel
          modules={modules}
          activeLessonId={activeLessonId}
          onCollapse={() => onToggle(false)}
          // Picking a lesson closes the tree, the open one included (the page ignores that pick).
          onSelectLesson={(id) => {
            onToggle(false);
            onSelectLesson?.(id);
          }}
          title={title}
        />
      </Box>
    );
  }
  return (
    <Box sx={{ flexShrink: 0, borderRight: `1px solid ${t.borderDim}`, height: '100%', bgcolor: t.bg }}>
      {expanded ? (
        <ExpandedPanel
          modules={modules}
          activeLessonId={activeLessonId}
          onCollapse={() => onToggle(false)}
          onSelectLesson={onSelectLesson}
        />
      ) : (
        <CollapsedRail modules={modules} onExpand={() => onToggle(true)} />
      )}
    </Box>
  );
};

export default LeftNav;
